import { Button, MenuItem, Position } from "@blueprintjs/core";
import React, { useLayoutEffect, useRef } from "react";
import { MultiSelect } from "@blueprintjs/select";
import "styled-components/macro";

const ENTER_RETURN_KEY_CODE = 13;

export type CustomerIssueCodeName =
  | "billing__general"
  | "billing__dispute"
  | "courses__general"
  | "courses__variety"
  | "courses__learning_style"
  | "courses__too_easy"
  | "courses__too_difficult"
  | "teachers__general"
  | "teachers__unresponsive"
  | "teachers__unprofessional"
  | "teachers__unengaging"
  | "student__bad_behavior"
  | "student__poor_performance"
  | "student__prefers_brick_and_mortar"
  | "student__graduated_high_school"
  | "student__illness"
  | "home_life__general"
  | "covid19__general"
  | "pricing__cannot_afford"
  | "other"
  | "no_issues";

export interface CustomerIssue {
  codeName: CustomerIssueCodeName;
  longName: string;
}

export const CustomerIssuesMS = MultiSelect.ofType<CustomerIssue>();

const DEFAULT_REASONS: CustomerIssue[] = [
  {
    codeName: "billing__general",
    longName: "Billing (General)",
  },
  {
    codeName: "billing__dispute",
    longName: "Billing (Dispute)",
  },
  { codeName: "pricing__cannot_afford", longName: "Pricing (Cannot Afford)" },
  { codeName: "courses__general", longName: "Courses (General)" },
  { codeName: "courses__variety", longName: "Courses (Variety)" },
  { codeName: "courses__too_easy", longName: "Courses (Too Easy)" },
  { codeName: "courses__too_difficult", longName: "Courses (Too Difficult)" },
  { codeName: "courses__learning_style", longName: "Courses (Learning Style)" },
  { codeName: "teachers__general", longName: "Teachers (General)" },
  { codeName: "teachers__unresponsive", longName: "Teachers (Unresponsive)" },
  {
    codeName: "teachers__unprofessional",
    longName: "Teachers (Unprofessional)",
  },
  { codeName: "teachers__unengaging", longName: "Teachers (Unengaging)" },
  { codeName: "student__bad_behavior", longName: "Student (Bad Behavior)" },
  {
    codeName: "student__poor_performance",
    longName: "Student (Poor Performance)",
  },
  {
    codeName: "student__prefers_brick_and_mortar",
    longName: "Student (Prefers Brick-&-Mortar)",
  },
  {
    codeName: "student__graduated_high_school",
    longName: "Student (Graduated High School)",
  },
  {
    codeName: "student__illness",
    longName: "Student (Illness)",
  },
  {
    codeName: "home_life__general",
    longName: "Home Life (General)",
  },
  { codeName: "covid19__general", longName: "Covid-19 (General)" },
  { codeName: "other", longName: "Other" },
  { codeName: "no_issues", longName: "No Issues" },
];

export function CustomerIssuesInput({
  queryText,
  setQueryText,
  selectedItems,
  onAdd,
  onClear,
  onRemove,
  activeField,
  setActiveField,
}) {
  const customerIssueInputRef = useRef(null);

  const handleFocus = () => {
    customerIssueInputRef.current.focus();
    setActiveField("customerIssues");
  };
  // const handleBlur = ev => {
  //   ev.preventDefault();
  //   customerIssueInputRef.current.blur();
  //   setActiveField(null);
  // };

  const clearButton =
    selectedItems.length > 0 ? (
      <Button icon="cross" minimal={true} onClick={onClear} />
    ) : undefined;
  const isItemSelected = (r) => selectedItems.includes(r);
  const onItemSelect = (customerIssue) => {
    if (isItemSelected(customerIssue)) {
      onRemove(customerIssue.codeName);
    } else {
      onAdd(customerIssue);
    }
  };
  const onItemsSelect = (customerIssues) => customerIssues.map(onItemSelect);
  useLayoutEffect(() => {
    // noinspection JSUnresolvedFunction
    handleFocus();
    // eslint-disable-next-line
  }, []);

  return (
    <div
      css={`
        .bp3-multi-select-popover .bp3-menu {
          //max-width: none !important;
          max-height: 220px !important;
          overflow-y: auto;
        }
      `}
    >
      <CustomerIssuesMS
        fill
        createNewItemFromQuery={undefined}
        onKeyDown={(ev) => {
          if (ev.keyCode === ENTER_RETURN_KEY_CODE) {
            ev.preventDefault();
          }
        }}
        resetOnSelect={true}
        createNewItemRenderer={null}
        openOnKeyDown={false}
        items={DEFAULT_REASONS}
        itemsEqual="codeName"
        itemDisabled={isItemSelected}
        itemPredicate={customerIssuesPredicate}
        itemRenderer={customerIssueItemRenderer(isItemSelected)}
        selectedItems={selectedItems}
        onItemSelect={onItemSelect}
        tagRenderer={customerIssueTagRenderer}
        onItemsPaste={onItemsSelect}
        placeholder="Choose all that apply..."
        query={queryText}
        onQueryChange={setQueryText}
        // onFocus={handleFocus}
        popoverProps={{
          usePortal: false,
          minimal: true,
          position: Position.BOTTOM_LEFT,
        }}
        tagInputProps={{
          onFocus: handleFocus,
          name: "customerIssues",
          autoFocus: true,
          inputRef: (r) => {
            customerIssueInputRef.current = r;
          },
          leftIcon: "diagnosis",
          large: true,
          onRemove: onRemove,
          rightElement: clearButton,
          tagProps: { large: false, minimal: true },
        }}
        // TODO: Center for IE11
        // css={`
        //   .bp3-popover-content .bp3-menu {
        //     width: ${width}px;
        //   }
        // `}
      />
    </div>
  );
}

const customerIssuesPredicate = (
  query,
  customerIssue: CustomerIssue,
  _index,
  exactMatch
) => {
  const normalizedTitle = customerIssue.longName.toLowerCase();
  const normalizedQuery = query.toLowerCase();

  if (exactMatch) {
    return normalizedTitle === normalizedQuery;
  } else {
    return normalizedTitle.includes(normalizedQuery);
  }
};
const customerIssueTagRenderer = (customerIssue: CustomerIssue) =>
  customerIssue.longName;
const customerIssueItemRenderer =
  (isItemSelected) =>
  (customerIssue: CustomerIssue, { modifiers, handleClick }) => {
    if (!modifiers.matchesPredicate) {
      return null;
    }
    const isSelected = isItemSelected(customerIssue);
    return (
      <MenuItem
        active={modifiers.active}
        icon={isSelected ? "tick" : "blank"}
        disabled={false}
        key={customerIssue.codeName}
        text={customerIssue.longName}
        // label={customerIssue.longName}
        onClick={handleClick}
        shouldDismissPopover={false}
      />
    );
  };
